import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { updateThemeData } from "../../functions/updateThemeData";
import "./themeUpdate.css";

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_AVATAR_IMAGES = 120;
const DAY_NAME_MAX_LEN = 20;
const DAY_KEYS = Array.from({ length: 9 }, (_, i) => `day${i + 1}`);
// The bonus, non-day-gated avatar bucket — always manageable on the Avatar
// tab, shown to players only when the "Allow SuperHero Ai" toggle is on.
const SUPERHERO_CATEGORY = "superheros";
// Days 1-9 are compulsory and can't be removed. Superheros is the built-in
// extra; admins can add more extras ("extra_..." keys) up to this total cap.
const MAX_EXTRA_CATEGORIES = 10;
const EXTRA_NAME_MAX_LEN = 20;

const TABS = [
  { id: "toggles",  label: "Day toggle" },
  { id: "avatar",   label: "Avatar" },
  { id: "settings", label: "Settings" },
  { id: "images",   label: "Images" },
  { id: "export",   label: "Export images" },
];

const AVATAR_IMAGE_SIZE = { width: 1400, height: 2048 };

// Images tab — banner/button graphics used across the player-facing pages.
// No exact-dimension requirement (unlike AVATAR_IMAGE_SIZE above): the
// frontend fits each one into its slot at whatever aspect ratio it has
// (width: auto; height: auto; capped by max-width/max-height), so any PNG
// works.
const IMAGE_FIELDS = [
  { key: "beginButtonImage",       label: "Begin button" },
  { key: "headingMainImage",       label: 'Heading main — "Create festive avatar" banner' },
  { key: "chooseAvatarHeadingImage", label: '"Choose festive avatar" banner' },
  { key: "livePreviewHeadingImage",  label: '"Live preview" banner' },
  { key: "useAvatarButtonImage",     label: '"Use avatar" button graphic' },
  { key: "festiveGalleryImage",      label: '"Festive Gallery" banner' },
  { key: "createAvatarButtonImage",  label: '"Create avatar" button graphic' },
  { key: "festiveAvatarHeadingImage", label: '"Here is your festive avatar" banner (final page)' },
  { key: "finalDownloadButtonImage",  label: '"Download" button graphic (final page)' },
  { key: "finalGalleryButtonImage",   label: '"View Gallery" button graphic (final page)' },
];

const parseObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

/** Encode each path segment (decode first to avoid double-encoding stored keys). */
const buildHostedAssetUrl = (baseEnv, relativePath) => {
  const base = String(baseEnv ?? "").replace(/\/+$/, "");
  const raw = String(relativePath ?? "").trim().replace(/^\/+/, "");
  if (!raw) return "";
  const encoded = raw
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
  return base ? `${base}/${encoded}` : encoded;
};

/** Prefix with numeric id so two uploads with same basename stay unique. */
const renameFileForUpload = (file) => {
  if (!file || !(file instanceof Blob)) return file;
  const original = file.name || "image";
  const lastDot = original.lastIndexOf(".");
  const ext = lastDot >= 0 ? original.slice(lastDot) : "";
  const base = lastDot >= 0 ? original.slice(0, lastDot) : original;
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  const newName = `${id}-${base}${ext}`;
  return new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified,
  });
};

// Lambda/API Gateway reject request bodies around 6 MB (413). Keep each
// avatar well under that by re-encoding big files as JPEG at the same pixel
// size (the exact-dimensions rule still holds), lowering quality until it fits.
const UPLOAD_TARGET_BYTES = 3 * 1024 * 1024;
const JPEG_QUALITY_STEPS = [0.92, 0.85, 0.78, 0.7, 0.6, 0.5];

const compressForUpload = (file) => new Promise((resolve) => {
  if (!file || file.size <= UPLOAD_TARGET_BYTES) {
    resolve(file);
    return;
  }
  const objectUrl = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; // JPEG has no alpha — flatten transparency onto white
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    const base = file.name.replace(/\.[^.]+$/, "");
    const tryQuality = (i) => {
      canvas.toBlob((blob) => {
        if (!blob) { resolve(file); return; }
        if (blob.size <= UPLOAD_TARGET_BYTES || i === JPEG_QUALITY_STEPS.length - 1) {
          resolve(new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: file.lastModified }));
        } else {
          tryQuality(i + 1);
        }
      }, "image/jpeg", JPEG_QUALITY_STEPS[i]);
    };
    tryQuality(0);
  };
  img.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(file);
  };
  img.src = objectUrl;
});

function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [characterThemes, setCharacterThemes] = useState({});
  const [avatarFiles, setAvatarFiles] = useState([]);
  const [avatarBlobSrcByName, setAvatarBlobSrcByName] = useState({});
  const [avatarMeta, setAvatarMeta] = useState({}); // { [filename]: { characterName, description } }
  const [pendingMeta, setPendingMeta] = useState({}); // { [pendingFile.name]: { characterName, description } }
  const [saving, setSaving] = useState(false);
  // Replacing an existing avatar's picture (not adding a new one) — keyed
  // by the OLD filename so its saved name/description/gender and its
  // position in the list survive the swap.
  const [replacementFiles, setReplacementFiles] = useState({}); // { [oldFilename]: File }
  const [replacementBlobSrcByName, setReplacementBlobSrcByName] = useState({});
  const [replaceTarget, setReplaceTarget] = useState(null);
  const replaceFileInputRef = useRef(null);
  const avatarFileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("toggles");

  // ── Day Toggle tab: per-day on/off + name + color, plus game-wide toggles ─
  const [dayToggles, setDayToggles] = useState({});
  const [dayLabels, setDayLabels] = useState({});
  const [dayColors, setDayColors] = useState({});
  const [savingToggles, setSavingToggles] = useState(false);
  // Which category the Avatar tab is currently managing avatars for —
  // one of day1..day9 or the bonus "superheros" bucket.
  const [avatarCategory, setAvatarCategory] = useState("day1");
  // Per-category "Description" toggle — when on, each avatar in that
  // category gets a description field (shown to players as the superpower
  // text). Off by default; keyed by category (day1..day9, superheros).
  const [avatarDescToggles, setAvatarDescToggles] = useState({});
  // Admin-added extra categories beyond day1..day9 and superheros —
  // [{ key: "extra_123", label: "Villains" }].
  const [extraCategories, setExtraCategories] = useState([]);

  // ── Images tab: banner/button graphics ──────────────────────────────────
  // Keyed by IMAGE_FIELDS' `key` — { [key]: File } / { [key]: blobUrl }.
  const [imageFiles, setImageFiles] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});
  const [savingImages, setSavingImages] = useState(false);

  // ── Export images tab ─────────────────────────────────────────────────
  const [exportingImages, setExportingImages] = useState(false);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setCharacterThemes(parseObject(data.character_theme));
    setAvatarMeta(parseObject(data.avatar_meta));
    setAvatarDescToggles(parseObject(data.avatar_desc_enabled));
    setExtraCategories(
      (Array.isArray(data.extra_categories) ? data.extra_categories : [])
        .filter((c) => c && typeof c.key === "string" && c.key)
    );

    setDayToggles(parseObject(data.day_toggles));
    setDayLabels(parseObject(data.day_labels));
    setDayColors(parseObject(data.day_colors));
  }, [data]);

  useLayoutEffect(() => {
    const next = {};
    avatarFiles.forEach((f) => {
      if (f?.name) next[f.name] = URL.createObjectURL(f);
    });
    setAvatarBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [avatarFiles]);

  useLayoutEffect(() => {
    const next = {};
    Object.entries(replacementFiles).forEach(([oldFilename, file]) => {
      if (file) next[oldFilename] = URL.createObjectURL(file);
    });
    setReplacementBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [replacementFiles]);

  // Avatar images are hosted under an "avatar/" subfolder (matches the
  // photobooth avatar-picker's fetch path).
  const getAvatarPreviewUrl = (fileName) => {
    if (!fileName) return "";
    return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", `avatar/${fileName}`);
  };

  // Non-avatar theme images (logo, background, and these two) are uploaded
  // to the root of the S3 path rather than the "avatar/" subfolder — this
  // mirrors how updateThemeMedia.js on the backend stores any file field
  // that isn't prefixed avatar_/good_/bad_.
  const getRootAssetUrl = (fileName) => {
    if (!fileName) return "";
    return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", fileName);
  };

  /** PNG-only validation for the Images tab — no dimension requirement,
      the frontend fits whatever size/aspect ratio is uploaded. */
  const validatePngOnly = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    return ext === "png" ? null : "Please upload a PNG image.";
  };

  const buildThemeFormData = (themesMap) => {
    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append(
      "extraData",
      JSON.stringify({
        themeName: data?.themeName || "",
        themeDescription: data?.themeDescription || "",
        character: themesMap[avatarCategory] || [],
        character_theme: themesMap,
        selected_character_theme: avatarCategory,
        timer: data?.timer || "05:00",
      })
    );
    return formData;
  };

  /** Extension + exact-dimensions check for avatar uploads — same policy as
      every other admin image upload (Begin button, Heading main, Thumbnail,
      Logo, Backgrounds): the admin's given dimension is the only size
      accepted, not just a ceiling. */
  const validateAvatarImage = (file) => new Promise((resolve) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      resolve(`Unsupported format for ${file.name}`);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== AVATAR_IMAGE_SIZE.width || img.height !== AVATAR_IMAGE_SIZE.height) {
        resolve(
          `${file.name} is ${img.width}x${img.height}px — avatar images must be exactly ${AVATAR_IMAGE_SIZE.width}x${AVATAR_IMAGE_SIZE.height}px.`
        );
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(`Could not read ${file.name}.`);
    };
    img.src = objectUrl;
  });

  // Uploads immediately on file selection — no separate Save click needed.
  // The chosen files render as "pending" tiles (existing blob-preview UI)
  // only for the brief window the upload is actually in flight.
  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;

    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    const existingCount = (characterThemes[avatarCategory] || []).length;
    const totalAfter = existingCount + selected.length;
    if (totalAfter > MAX_AVATAR_IMAGES) {
      Swal.fire(
        "Limit exceeded",
        `You can add up to ${MAX_AVATAR_IMAGES} avatar images, and only ${Math.max(MAX_AVATAR_IMAGES - existingCount, 0)} more can be added right now.`,
        "warning"
      );
      return;
    }

    const errors = (await Promise.all(selected.map(validateAvatarImage))).filter(Boolean);
    if (errors.length) {
      Swal.fire("Invalid file", errors[0], "error");
      return;
    }

    setSaving(true);
    const renamed = (await Promise.all(selected.map(compressForUpload))).map(renameFileForUpload);
    setAvatarFiles(renamed);
    try {
      // One file per request: API Gateway/Lambda cap a request at ~6 MB, so
      // a multi-select of several large avatars in a single multipart body
      // fails with "413 Content Too Large". The backend appends each upload
      // to the category array it's sent, so re-read the saved map between
      // uploads to avoid overwriting the previous file with a stale list.
      let themesMap = characterThemes;
      for (const file of renamed) {
        const formData = buildThemeFormData(themesMap);
        formData.append("avatarFiles", file);
        await uploadThemeMedia({ formData, token: adminToken });
        const refreshed = await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        themesMap = parseObject(refreshed?.data?.character_theme);
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to upload avatar", "error");
    } finally {
      setAvatarFiles([]);
      setSaving(false);
    }
  };

  // ── Replace an existing avatar's picture in place ──────────────────────
  const triggerReplaceImage = (oldFilename) => {
    setReplaceTarget(oldFilename);
    replaceFileInputRef.current?.click();
  };

  // Also uploads immediately — picking a replacement photo swaps it in place
  // right away instead of waiting for a separate Save click.
  const handleReplaceFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;

    const error = await validateAvatarImage(file);
    if (error) {
      Swal.fire("Invalid file", error, "error");
      setReplaceTarget(null);
      return;
    }
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      setReplaceTarget(null);
      return;
    }

    const oldFilename = replaceTarget;
    const renamedFile = renameFileForUpload(await compressForUpload(file));
    setReplacementFiles((prev) => ({ ...prev, [oldFilename]: renamedFile }));
    setReplaceTarget(null);
    setSaving(true);
    try {
      const formData = buildThemeFormData(characterThemes);
      formData.append("avatarFiles", renamedFile);
      await uploadThemeMedia({ formData, token: adminToken });
      const refreshed = await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      // The upload above appended the new file to the tail of the array —
      // move it back into the replaced avatar's original position.
      const newThemes = parseObject(refreshed?.data?.character_theme);
      const newImages = newThemes[avatarCategory] || [];
      const newFilename = newImages[newImages.length - 1];
      const correctedThemeArray = newImages.slice(0, -1);
      const idx = correctedThemeArray.indexOf(oldFilename);
      if (idx !== -1) {
        correctedThemeArray[idx] = newFilename;
      } else {
        correctedThemeArray.push(newFilename);
      }

      await uploadThemeMedia({
        formData: buildThemeFormData({ ...newThemes, [avatarCategory]: correctedThemeArray }),
        token: adminToken,
      });

      // avatar_meta is keyed by filename, so carry the replaced avatar's
      // gender/description over to the new file or it would silently drop.
      if (avatarMeta[oldFilename]) {
        const { [oldFilename]: movedMeta, ...otherMeta } = avatarMeta;
        await updateThemeData({
          payload: { data: { avatar_meta: { ...otherMeta, [newFilename]: movedMeta } }, currentTheme },
          token: adminToken,
        });
      }
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to replace avatar", "error");
    } finally {
      setReplacementFiles((prev) => {
        const { [oldFilename]: _dropped, ...rest } = prev;
        return rest;
      });
      setSaving(false);
    }
  };

  const removePendingFile = (index) => {
    setAvatarFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        setPendingMeta((prevMeta) => {
          const { [removed.name]: _dropped, ...rest } = prevMeta;
          return rest;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (index) => {
    setCharacterThemes((prev) => {
      const removedName = (prev[avatarCategory] || [])[index];
      if (removedName) {
        setAvatarMeta((prevMeta) => {
          const { [removedName]: _dropped, ...rest } = prevMeta;
          return rest;
        });
      }
      return {
        ...prev,
        [avatarCategory]: (prev[avatarCategory] || []).filter((_, i) => i !== index),
      };
    });
  };

  const updateAvatarDescription = (filename, value) => {
    setAvatarMeta((prev) => ({
      ...prev,
      [filename]: { ...prev[filename], description: value.slice(0, 200) },
    }));
  };

  // Empty gender = shown to every player; "male"/"female" limits the avatar
  // to players who picked that gender on the game's photo page.
  const updateAvatarGender = (filename, value) => {
    setAvatarMeta((prev) => ({
      ...prev,
      [filename]: { ...prev[filename], gender: value },
    }));
  };

  // Extra/Superhero avatars only: tie an avatar to one day (color) so players
  // on that day see it. Empty = shown on every day.
  const updateAvatarDay = (filename, value) => {
    setAvatarMeta((prev) => ({
      ...prev,
      [filename]: { ...prev[filename], day: value },
    }));
  };

  const toggleAvatarDescForCategory = () => {
    setAvatarDescToggles((prev) => ({ ...prev, [avatarCategory]: prev[avatarCategory] !== true }));
  };

  const avatarCategoryTabs = [
    ...DAY_KEYS,
    SUPERHERO_CATEGORY,
    ...extraCategories.map((c) => c.key),
  ];
  const categoryLabel = (key) => {
    if (key === SUPERHERO_CATEGORY) return "Superheros";
    const extra = extraCategories.find((c) => c.key === key);
    if (extra) return extra.label;
    return dayLabels[key] || `Day ${key.replace("day", "")}`;
  };

  const resetPendingAvatarState = () => {
    setAvatarFiles([]);
    setPendingMeta({});
    setReplacementFiles({});
  };

  const handleAddCategory = async () => {
    if (extraCategories.length >= MAX_EXTRA_CATEGORIES) {
      Swal.fire("Limit reached", `You can have at most ${MAX_EXTRA_CATEGORIES} extra categories (Days 1-9 are compulsory).`, "warning");
      return;
    }
    const { value } = await Swal.fire({
      title: "Add avatar category",
      input: "text",
      inputPlaceholder: "e.g. Villains",
      inputAttributes: { maxlength: EXTRA_NAME_MAX_LEN },
      showCancelButton: true,
      confirmButtonText: "Add",
      inputValidator: (v) => (v && v.trim() ? null : "Enter a category name"),
    });
    const label = (value || "").trim().slice(0, EXTRA_NAME_MAX_LEN);
    if (!label) return;

    const next = [...extraCategories, { key: `extra_${Date.now()}`, label }];
    setSaving(true);
    try {
      await updateThemeData({ payload: { data: { extra_categories: next }, currentTheme }, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setAvatarCategory(next[next.length - 1].key);
      resetPendingAvatarState();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to add category", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCategory = async (key) => {
    const extra = extraCategories.find((c) => c.key === key);
    if (!extra) return;
    const { isConfirmed } = await Swal.fire({
      title: `Remove "${extra.label}"?`,
      text: "All avatars in this category will be removed too.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
    });
    if (!isConfirmed) return;

    const removedFiles = characterThemes[key] || [];
    const { [key]: _droppedImages, ...restThemes } = characterThemes;
    const { [key]: _droppedToggle, ...restToggles } = avatarDescToggles;
    const restMeta = { ...avatarMeta };
    removedFiles.forEach((f) => delete restMeta[f]);
    const next = extraCategories.filter((c) => c.key !== key);

    setSaving(true);
    try {
      await uploadThemeMedia({ formData: buildThemeFormData(restThemes), token: adminToken });
      await updateThemeData({
        payload: {
          data: { extra_categories: next, avatar_meta: restMeta, avatar_desc_enabled: restToggles },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setAvatarCategory("day1");
      resetPendingAvatarState();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to remove category", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    setSaving(true);
    try {
      const replacementEntries = Object.entries(replacementFiles); // [[oldFilename, file], ...]

      const formData = buildThemeFormData(characterThemes);
      avatarFiles.forEach((file) => formData.append("avatarFiles", file));
      // Replacement images ride along in the same upload call — the backend
      // always appends new uploads to the tail of the array, so they land
      // in the wrong spot at first; the correction below moves each one
      // back into its original avatar's position right after.
      replacementEntries.forEach(([, file]) => formData.append("avatarFiles", file));

      await uploadThemeMedia({ formData, token: adminToken });
      const refreshed = await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      // Newly uploaded files get a server-assigned filename we can't predict
      // client-side, so map the pending metadata onto the tail of the
      // freshly-saved image list (uploads are always appended in order —
      // new avatars first, then replacements, matching the append order
      // above).
      const nextAvatarMeta = { ...avatarMeta };
      const totalUploaded = avatarFiles.length + replacementEntries.length;
      let correctedThemeArray = null;

      if (totalUploaded) {
        const newThemes = parseObject(refreshed?.data?.character_theme);
        const newImages = newThemes[avatarCategory] || [];
        const addedFilenames = newImages.slice(-totalUploaded);

        const newlyAddedFilenames = addedFilenames.slice(0, avatarFiles.length);
        newlyAddedFilenames.forEach((filename, i) => {
          const pendingFile = avatarFiles[i];
          const meta = pendingFile ? pendingMeta[pendingFile.name] : null;
          if (meta && (meta.characterName || meta.description)) {
            nextAvatarMeta[filename] = { ...meta };
          }
        });

        if (replacementEntries.length) {
          const replacementNewFilenames = addedFilenames.slice(avatarFiles.length);
          // Drop the replacement uploads off the tail — they belong at
          // their original avatar's index, not appended as new entries.
          correctedThemeArray = newImages.slice(0, newImages.length - replacementEntries.length);

          replacementEntries.forEach(([oldFilename], i) => {
            const newFilename = replacementNewFilenames[i];
            if (!newFilename) return;
            const idx = correctedThemeArray.indexOf(oldFilename);
            if (idx !== -1) {
              correctedThemeArray[idx] = newFilename;
            } else {
              correctedThemeArray.push(newFilename);
            }
            if (nextAvatarMeta[oldFilename]) {
              nextAvatarMeta[newFilename] = nextAvatarMeta[oldFilename];
              delete nextAvatarMeta[oldFilename];
            }
          });

          await uploadThemeMedia({
            formData: buildThemeFormData({ ...newThemes, [avatarCategory]: correctedThemeArray }),
            token: adminToken,
          });
        }
      }

      await updateThemeData({
        payload: { data: { avatar_meta: nextAvatarMeta, avatar_desc_enabled: avatarDescToggles }, currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      setAvatarFiles([]);
      setPendingMeta({});
      setReplacementFiles({});
      Swal.fire({ title: "Success", text: "Theme data updated", icon: "success", timer: 3000, timerProgressBar: true });
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update theme data", "error");
    } finally {
      setSaving(false);
    }
  };


  // ── Images tab ──────────────────────────────────────────────────────────
  const handleImageFieldChange = (key) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const error = validatePngOnly(file);
    if (error) {
      Swal.fire("Invalid image", error, "error");
      return;
    }
    setImageFiles((prev) => ({ ...prev, [key]: file }));
    setImagePreviews((prev) => ({ ...prev, [key]: URL.createObjectURL(file) }));
  };

  const handleSaveImages = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }
    const pendingEntries = Object.entries(imageFiles).filter(([, file]) => file);
    if (!pendingEntries.length) {
      Swal.fire("Nothing to save", "Choose a new image first.", "info");
      return;
    }

    setSavingImages(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName || "",
          themeDescription: data?.themeDescription || "",
        })
      );
      pendingEntries.forEach(([key, file]) => formData.append(key, file));

      await uploadThemeMedia({ formData, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      setImageFiles({});
      setImagePreviews({});
      Swal.fire({ title: "Success", text: "Images updated", icon: "success", timer: 3000, timerProgressBar: true });
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update images", "error");
    } finally {
      setSavingImages(false);
    }
  };

  // The backend builds the zip, uploads it to S3, and hands back a small
  // presigned downloadUrl — the zip itself never passes through this
  // fetch (Lambda's response is capped at 6MB, easily blown past by a zip
  // of several photos). Navigating to that S3 URL directly downloads it,
  // since S3 sets Content-Disposition:attachment itself — no blob/CORS
  // handling needed for the actual file, only for this small JSON call.
  const handleExportImages = async () => {
    setExportingImages(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/exportGalleryImages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok || !body.downloadUrl) {
        throw new Error(body.error || "Failed to export images");
      }

      const a = document.createElement("a");
      a.href = body.downloadUrl;
      a.download = "festive_avatars.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to export images", "error");
    } finally {
      setExportingImages(false);
    }
  };

  // ── Day Toggle / game toggles ────────────────────────────────────────────
  const toggleDay = (key) => {
    setDayToggles((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const updateDayLabel = (day, value) => {
    setDayLabels((prev) => ({ ...prev, [day]: value }));
  };

  const updateDayColor = (day, value) => {
    setDayColors((prev) => ({ ...prev, [day]: value }));
  };

  const handleSaveToggles = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    // The color <input> shows white as a display placeholder for any day the
    // admin hasn't actively picked a color for yet (value={dayColors[day] ||
    // "#ffffff"}), but that placeholder never gets written to `dayColors`
    // state unless the input's onChange actually fires — so an untouched
    // day silently saved as "unset" and rendered as the player-facing
    // fallback grey (#cccccc) instead of the white shown here. Fill in the
    // same default before saving so what the admin sees is what's stored.
    const normalizedDayColors = DAY_KEYS.reduce(
      (acc, day) => ({ ...acc, [day]: dayColors[day] || "#ffffff" }),
      {}
    );

    setSavingToggles(true);
    try {
      await updateThemeData({
        payload: {
          data: { day_toggles: dayToggles, day_labels: dayLabels, day_colors: normalizedDayColors },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Success", text: "Day toggle settings updated", icon: "success", timer: 3000, timerProgressBar: true });
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update day toggles", "error");
    } finally {
      setSavingToggles(false);
    }
  };

  const [savingGameToggles, setSavingGameToggles] = useState(false);
  const handleSaveGameToggles = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    // At least one avatar source has to stay available to players — both
    // Navratri and Superhero AI can be on, but turning both off would leave
    // no avatars to offer at all.
    if (dayToggles.navratri_ai === false && dayToggles.superhero_Ai === false) {
      Swal.fire(
        "Invalid toggle combination",
        "Allow Navratri AI and Allow superhero AI can't both be off — at least one must stay on.",
        "warning"
      );
      return;
    }

    setSavingGameToggles(true);
    try {
      await updateThemeData({
        payload: { data: { day_toggles: dayToggles }, currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire({ title: "Success", text: "Settings updated", icon: "success", timer: 3000, timerProgressBar: true });
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update settings", "error");
    } finally {
      setSavingGameToggles(false);
    }
  };

  const activeImages = characterThemes[avatarCategory] || [];

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="theme-tabs-row">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`theme-tab-btn${activeTab === tab.id ? " theme-tab-btn-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="theme-data-page">
        <div className="theme-data-grid">
          {activeTab === "avatar" && (
          <section className="theme-data-card">
            <h3>Avatar character set</h3>

            <div className="theme-instruction-text">
              You can add up to {MAX_AVATAR_IMAGES} characters per category. Each image must be JPG/JPEG/PNG and exactly {AVATAR_IMAGE_SIZE.width}x{AVATAR_IMAGE_SIZE.height}px.
            </div>

            <div className="theme-tabs-row theme-day-picker-row">
              {avatarCategoryTabs.map((day) => (
                <button
                  key={day}
                  type="button"
                  className={`theme-tab-btn${avatarCategory === day ? " theme-tab-btn-active" : ""}`}
                  onClick={() => {
                    if (avatarCategory === day) return;
                    setAvatarCategory(day);
                    resetPendingAvatarState();
                  }}
                >
                  {categoryLabel(day)}
                </button>
              ))}
              <button
                type="button"
                className="theme-tab-btn"
                onClick={handleAddCategory}
                disabled={saving || extraCategories.length >= MAX_EXTRA_CATEGORIES}
                title={`Add a category (${extraCategories.length}/${MAX_EXTRA_CATEGORIES})`}
              >
                + Add category
              </button>
            </div>

            {avatarCategory.startsWith("extra_") && (
              <div className="gallery-toggle-row">
                <span>Extra category "{categoryLabel(avatarCategory)}" — {extraCategories.length}/{MAX_EXTRA_CATEGORIES} extras used</span>
                <button type="button" className="theme-tab-btn" onClick={() => handleRemoveCategory(avatarCategory)} disabled={saving}>
                  Remove category
                </button>
              </div>
            )}

            <div className="gallery-toggle-row">
              <span>
                {avatarDescToggles[avatarCategory] === true
                  ? "Each avatar in this category has a description field"
                  : "Description field is off for this category's avatars"}
              </span>
              <label className="theme-toggle">
                <input
                  type="checkbox"
                  checked={avatarDescToggles[avatarCategory] === true}
                  onChange={toggleAvatarDescForCategory}
                />
                <span className="theme-toggle-slider" />
              </label>
            </div>

            <input
              ref={avatarFileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <input
              ref={replaceFileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleReplaceFileChange}
              style={{ display: "none" }}
            />

            <div className="theme-data-list">
              <button
                type="button"
                className="theme-avatar-add-tile"
                onClick={() => avatarFileInputRef.current?.click()}
                disabled={saving}
              >
                <span className="theme-avatar-add-icon">+</span>
                <span className="theme-avatar-add-text">{saving ? "Uploading…" : "Add avatar"}</span>
                <span className="theme-avatar-add-sub">
                  JPG/JPEG/PNG · up to {MAX_AVATAR_IMAGES} · exactly {AVATAR_IMAGE_SIZE.width}x{AVATAR_IMAGE_SIZE.height}px
                </span>
              </button>

              {activeImages.map((name, index) => {
                const hasReplacement = Object.prototype.hasOwnProperty.call(replacementFiles, name);
                return (
                    <div className={`theme-data-item theme-avatar-item${hasReplacement ? " theme-data-item-replacing" : ""}`} key={`${name}-${index}`}>
                      <div className="theme-data-item-img-wrap">
                        <img src={hasReplacement ? replacementBlobSrcByName[name] : getAvatarPreviewUrl(name)} alt={name} />
                      </div>
                      <button onClick={() => removeExistingImage(index)} disabled={saving}>&times;</button>
                      {hasReplacement ? (
                        <div className="theme-data-item-replace-row">
                          <span className="theme-data-item-replace-note">{saving ? "Uploading…" : "Saved"}</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="theme-data-item-replace-btn"
                          onClick={() => triggerReplaceImage(name)}
                          disabled={saving}
                        >
                          Change photo
                        </button>
                      )}
                      {dayToggles.allow_gender !== false && (
                        <select
                          className="theme-data-item-gender"
                          aria-label="Avatar gender"
                          value={avatarMeta[name]?.gender || ""}
                          onChange={(e) => updateAvatarGender(name, e.target.value)}
                        >
                          <option value="">Both (male &amp; female)</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                        </select>
                      )}
                      {(avatarCategory === SUPERHERO_CATEGORY || avatarCategory.startsWith("extra_")) && (
                        <select
                          className="theme-data-item-gender"
                          aria-label="Avatar day"
                          value={avatarMeta[name]?.day || ""}
                          onChange={(e) => updateAvatarDay(name, e.target.value)}
                        >
                          <option value="">All days</option>
                          {DAY_KEYS.map((d) => (
                            <option key={d} value={d}>
                              {`Day ${d.replace("day", "")}${dayLabels[d] ? ` – ${dayLabels[d]}` : ""}`}
                            </option>
                          ))}
                        </select>
                      )}
                      {avatarDescToggles[avatarCategory] === true && (
                        <>
                          <textarea
                            className="theme-data-item-desc"
                            placeholder="Description (shown to players)"
                            maxLength={200}
                            value={avatarMeta[name]?.description || ""}
                            onChange={(e) => updateAvatarDescription(name, e.target.value)}
                          />
                          <div className="theme-data-item-count">
                            {(avatarMeta[name]?.description || "").length}/200
                          </div>
                        </>
                      )}
                    </div>
                );
              })}
              {avatarFiles.map((file, index) => (
                <div className="theme-data-item theme-avatar-item theme-data-item-pending" key={`pending-${file.name}-${index}`}>
                  <div className="theme-data-item-img-wrap">
                    <img src={avatarBlobSrcByName[file.name]} alt={file.name} />
                  </div>
                  <span className="theme-data-item-replace-note">{saving ? "Uploading…" : ""}</span>
                  <button onClick={() => removePendingFile(index)} disabled={saving}>&times;</button>
                </div>
              ))}
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "settings" && (
          <section className="theme-data-card">
            <h3>Game toggles</h3>

            <div className="theme-toggle-grid">
              {[
                ["uploads", "Enable uploads", "Players can submit entries", "Uploads are closed — players see a closed message instead of the form"],
                ["multiple_entry", "Multiple entry", "Players can submit multiple entries", "Each player can only submit once — repeat visits show their existing result"],
                ["allow_selfie", "Allow taking a selfie", "Players can upload/take a photo, or pick a ready-made avatar", "Players can only pick a ready-made avatar (no photo upload/selfie)"],
                ["navratri_ai", "Allow Navratri AI", "The 9 Navratri days are offered to players", "Navratri day avatars are hidden from players"],
                ["superhero_Ai", "Allow superhero AI", "The bonus Superhero avatar set is offered to players", "Superhero avatars are hidden from players"],
                ["allow_gender", "Allow gender", "The gender field is shown to players", "The gender field is hidden from players"],
              ].map(([key, label, onText, offText]) => (
                <div className="theme-toggle-card" key={key}>
                  <div className="theme-toggle-card-head">
                    <strong>{label}</strong>
                    <label className="theme-toggle">
                      <input
                        type="checkbox"
                        checked={dayToggles[key] !== false}
                        onChange={() => toggleDay(key)}
                      />
                      <span className="theme-toggle-slider" />
                    </label>
                  </div>
                  <span className="theme-toggle-card-text">
                    {dayToggles[key] === false ? offText : onText}
                  </span>

                  {key === "multiple_entry" && dayToggles.multiple_entry !== false && (
                    <div className="theme-toggle-card-extra">
                      <label htmlFor="entry-limit">Entry limit per user</label>
                      <input
                        id="entry-limit"
                        type="number"
                        min="1"
                        placeholder="Unlimited"
                        value={dayToggles.entry_limit ?? ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setDayToggles((prev) => ({
                            ...prev,
                            entry_limit: val === "" ? "" : Math.max(1, parseInt(val, 10) || 1),
                          }));
                        }}
                      />
                      <span className="theme-data-helper-text">Leave blank for unlimited entries.</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSaveGameToggles} disabled={savingGameToggles}>
                {savingGameToggles ? "Saving..." : "Save toggles"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "images" && (
          <section className="theme-data-card">
            <h3>Images</h3>

            <div className="theme-instruction-text">
              PNG only — any size or aspect ratio works, each image is scaled to fit its spot on the page without cropping.
            </div>

            <div className="theme-settings-image-row">
              {IMAGE_FIELDS.map(({ key, label }) => (
                <div className="theme-settings-image-field" key={key}>
                  <label className="theme-settings-image-label">{label} (PNG only)</label>
                  <div className="theme-settings-image-preview">
                    {(imagePreviews[key] || data?.[key]) ? (
                      <img
                        src={imagePreviews[key] || getRootAssetUrl(data[key])}
                        alt={label}
                      />
                    ) : (
                      <span className="theme-settings-image-empty">No image uploaded yet</span>
                    )}
                  </div>
                  <input type="file" accept=".png" onChange={handleImageFieldChange(key)} />
                </div>
              ))}
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSaveImages} disabled={savingImages}>
                {savingImages ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "export" && (
          <section className="theme-data-card">
            <h3>Export images</h3>

            <div className="theme-instruction-text">
              Downloads every player-submitted festive avatar for this session as one ZIP file (a single folder inside it, one image per player).
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleExportImages} disabled={exportingImages}>
                {exportingImages ? "Preparing ZIP..." : "Download all images (.zip)"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "toggles" && (
          <section className="theme-data-card">
            <h3>Day toggle</h3>

            <div className="theme-data-helper-text">
              Turn a day off to hide it from players immediately — existing entries for that day are kept. Set a name and color for each day (e.g. "Day 1 - White").
            </div>

            <div className="theme-instruction-text">
              Day name can be at most {DAY_NAME_MAX_LEN} characters. Each day needs at least one avatar character uploaded (Avatar tab) and enabled before players can pick it.
            </div>

            <div className="theme-data-list">
              {DAY_KEYS.map((day) => (
                <div className="theme-data-item theme-day-toggle-item" key={day}>
                  <div className="theme-day-toggle-row">
                    <input
                      type="color"
                      className="theme-day-color-input"
                      value={dayColors[day] || "#ffffff"}
                      onChange={(e) => updateDayColor(day, e.target.value)}
                      title="Day color"
                    />
                    <input
                      type="text"
                      className="theme-data-item-name"
                      placeholder={`Day ${day.replace("day", "")} name`}
                      maxLength={DAY_NAME_MAX_LEN}
                      value={dayLabels[day] || ""}
                      onChange={(e) => updateDayLabel(day, e.target.value)}
                    />
                  </div>
                  <div className="gallery-toggle-row">
                    <span>{dayToggles[day] === false ? "Off" : "On"}</span>
                    <label className="theme-toggle">
                      <input
                        type="checkbox"
                        checked={dayToggles[day] !== false}
                        onChange={() => toggleDay(day)}
                      />
                      <span className="theme-toggle-slider" />
                    </label>
                  </div>
                </div>
              ))}
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSaveToggles} disabled={savingToggles}>
                {savingToggles ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}
        </div>
      </div>
    </>
  );
}

export default ThemeUpdate;
